import {
    type ConversationStreamState,
    type StreamRunNode,
    type StreamRunState,
} from '@apps/web/components/phoebe_chat/streaming_types';
import { parseTimestampMs } from '@apps/web/components/phoebe_chat_view/run_status/duration_format';

export const EMPTY_STREAM_STATE: ConversationStreamState = {
    conversationId: null,
    currentRunId: null,
    runsById: {},
};

// Websocket stream timestamps and committed run_started timestamps are both
// server-generated but written by different services; allow a small skew when
// deciding whether the overlay belongs to the latest committed pass.
export const STREAM_OVERLAY_RUN_STARTED_TOLERANCE_MS = 5_000;

/**
 * Decide whether the websocket stream overlay may render — and suppress
 * committed transcript events — for the latest run pass.
 *
 * The overlay's run entry is keyed by the durable run id and survives across
 * LLM passes, while a later pass may stream no websocket content at all. If
 * the transcript filter trusted such a stale overlay, the newer pass's
 * committed response would be rendered by neither layer (prod incident
 * 2026-07-28, run 019faa1d-5e37). The committed event feed is authoritative:
 * an overlay with no content at or after the latest committed run_started is
 * dropped, and the transcript renders unfiltered.
 */
export function resolveStreamOverlay({
    displayStreamRun,
    latestRunStartedOffset,
    latestRunStartedAtMs,
}: {
    readonly displayStreamRun: StreamRunState | null;
    readonly latestRunStartedOffset: number | null;
    readonly latestRunStartedAtMs: number | null;
}): {
    readonly overlayRun: StreamRunState | null;
    readonly filterBoundaryOffset: number | null;
} {
    if (!displayStreamRun) {
        return { overlayRun: null, filterBoundaryOffset: null };
    }
    // No committed pass boundary yet (first turn still streaming): nothing to
    // filter and the overlay is the only representation of the work.
    if (latestRunStartedOffset === null) {
        return { overlayRun: displayStreamRun, filterBoundaryOffset: null };
    }
    if (latestRunStartedAtMs === null) {
        return {
            overlayRun: displayStreamRun,
            filterBoundaryOffset: latestRunStartedOffset,
        };
    }

    // Overlay newer than every committed run_started: its own boundary event
    // has not committed yet, so there is nothing to filter — hiding entries
    // behind the stale boundary would blank prior history.
    const startedAtMs = parseTimestampMs(displayStreamRun.startedAt);
    if (
        startedAtMs !== null &&
        startedAtMs >
            latestRunStartedAtMs + STREAM_OVERLAY_RUN_STARTED_TOLERANCE_MS
    ) {
        return { overlayRun: displayStreamRun, filterBoundaryOffset: null };
    }

    const coverageBoundaryMs =
        latestRunStartedAtMs - STREAM_OVERLAY_RUN_STARTED_TOLERANCE_MS;
    const overlayCoversLatestPass =
        (startedAtMs !== null && startedAtMs >= coverageBoundaryMs) ||
        displayStreamRun.orderedNodeIds.some((nodeId) => {
            const node = displayStreamRun.nodesById[nodeId];
            const nodeCreatedAtMs =
                node === undefined ? null : parseTimestampMs(node.createdAt);
            return (
                nodeCreatedAtMs !== null &&
                nodeCreatedAtMs >= coverageBoundaryMs
            );
        });
    if (!overlayCoversLatestPass) {
        return { overlayRun: null, filterBoundaryOffset: null };
    }
    return {
        overlayRun: displayStreamRun,
        filterBoundaryOffset: latestRunStartedOffset,
    };
}

export function getDisplayStreamRun<TRun extends { readonly status: string }>({
    currentStreamRun,
    retainedStreamRun,
    isAgentThinking,
}: {
    readonly currentStreamRun: TRun | null;
    readonly retainedStreamRun: TRun | null;
    readonly isAgentThinking: boolean;
}): TRun | null {
    if (currentStreamRun) {
        return currentStreamRun;
    }

    if (isAgentThinking && retainedStreamRun?.status === 'running') {
        return retainedStreamRun;
    }

    return null;
}

export function createStreamRunState(
    runId: string,
    requestTurnId: string | null,
    startedAt: string,
): StreamRunState {
    return {
        runId,
        requestTurnId,
        startedAt,
        status: 'running',
        nodesById: {},
        orderedNodeIds: [],
    };
}

export function upsertStreamRunNode(
    run: StreamRunState,
    node: StreamRunNode,
): StreamRunState {
    const existing = run.nodesById[node.id];
    const mergedNode: StreamRunNode = existing
        ? {
              ...existing,
              ...node,
              content: existing.content + node.content,
              arguments: node.arguments ?? existing.arguments,
              result: node.result ?? existing.result,
              error: node.error ?? existing.error,
              status: node.status ?? existing.status,
              isComplete: existing.isComplete || node.isComplete,
              durationMs: node.durationMs ?? existing.durationMs,
              responseKind: node.responseKind ?? existing.responseKind,
          }
        : node;

    const nodesById = {
        ...run.nodesById,
        [node.id]: mergedNode,
    };

    const orderedNodeIds = run.orderedNodeIds.includes(node.id)
        ? [...run.orderedNodeIds]
        : [...run.orderedNodeIds, node.id];

    orderedNodeIds.sort((leftId, rightId) => {
        const left = nodesById[leftId];
        const right = nodesById[rightId];
        if (!left || !right) {
            return leftId.localeCompare(rightId);
        }
        if (left.sequence !== right.sequence) {
            return left.sequence - right.sequence;
        }
        if (left.createdAt !== right.createdAt) {
            return left.createdAt.localeCompare(right.createdAt);
        }
        return left.id.localeCompare(right.id);
    });

    return {
        ...run,
        nodesById,
        orderedNodeIds,
    };
}
